import { Eye, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import type { AdminUserSummary } from "@/lib/api";
import { cn } from "@/lib/utils";

import { UserDeleteDialog } from "./user-delete-dialog";
import { UserDetailDialog } from "./user-detail-dialog";
import { UserEditDialog } from "./user-edit-dialog";

interface UserTableProps {
  users: AdminUserSummary[];
}

const HEADER_CELL = "text-muted px-5 py-3 text-left text-[13px] font-semibold";

export function UserTable({ users }: UserTableProps) {
  const [viewUserId, setViewUserId] = useState<string | null>(null);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [deleteUser, setDeleteUser] = useState<AdminUserSummary | null>(null);

  if (users.length === 0) {
    return (
      <div className="border-border rounded-2xl border bg-white py-12 text-center">
        <p className="text-muted text-[15px]">No users found.</p>
      </div>
    );
  }

  return (
    <>
      <div className="border-border overflow-hidden rounded-2xl border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-border border-b">
                <th className={HEADER_CELL}>Name</th>
                <th className={HEADER_CELL}>Email</th>
                <th className={HEADER_CELL}>Role</th>
                <th className={HEADER_CELL}>Joined</th>
                <th className={cn(HEADER_CELL, "text-right")}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.id}
                  className="border-border hover:bg-background border-b transition-colors last:border-0"
                >
                  <td className="text-primary px-5 py-4 text-[15px] font-medium">
                    {user.first_name || user.last_name
                      ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
                      : "—"}
                  </td>
                  <td className="text-muted px-5 py-4 text-[15px]">{user.email}</td>
                  <td className="px-5 py-4">
                    <span
                      className={cn(
                        "inline-flex rounded-sm px-2.5 py-1 text-[13px] font-semibold capitalize",
                        user.role === "admin"
                          ? "bg-accent/10 text-accent"
                          : "bg-surface-subtle text-muted",
                      )}
                    >
                      {user.role}
                    </span>
                  </td>
                  <td className="text-muted px-5 py-4 text-[15px]">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="View user"
                        onClick={() => setViewUserId(user.id)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Edit user"
                        onClick={() => setEditUserId(user.id)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Delete user"
                        onClick={() => setDeleteUser(user)}
                      >
                        <Trash2 className="text-destructive h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <UserDetailDialog
        userId={viewUserId}
        open={!!viewUserId}
        onOpenChange={(open) => !open && setViewUserId(null)}
      />
      <UserEditDialog
        userId={editUserId}
        open={!!editUserId}
        onOpenChange={(open) => !open && setEditUserId(null)}
      />
      <UserDeleteDialog
        user={deleteUser}
        open={!!deleteUser}
        onOpenChange={(open) => !open && setDeleteUser(null)}
      />
    </>
  );
}
